import React, { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Header from './components/common/Header';
import SiteNav from './components/common/SiteNav';
import MetricCard from './components/cards/MetricCard';
import TimelineChart from './components/charts/TimelineChart';
import DwellLadderCard from './components/cards/DwellLadderCard';
import ActionLeaderboard from './components/cards/ActionLeaderboard';
import TopContentCard from './components/cards/TopContentCard';
import DiscoveryCard from './components/cards/DiscoveryCard';
import LiveFeedCard from './components/cards/LiveFeedCard';
import LoginScreen from './components/LoginScreen';
import { SITES, getSiteById } from './config/sites';
import { Users, Eye, Zap, Timer, ArrowUpRight, ShieldAlert } from 'lucide-react';

function DashboardContent() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [selectedSite, setSelectedSiteState] = useState(() => {
    try {
      const saved = localStorage.getItem('topazly_selected_site');
      if (saved && SITES.some((s) => s.id === saved)) return saved;
    } catch {}
    return 'all';
  });
  const [selectedRange, setSelectedRangeState] = useState(() => {
    try {
      const saved = localStorage.getItem('topazly_timeframe');
      if (saved && ['1h', '24h', '7d', '30d'].includes(saved)) return saved;
    } catch {}
    return '24h';
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const setSelectedSite = (site) => {
    setSelectedSiteState(site);
    try {
      localStorage.setItem('topazly_selected_site', site);
    } catch {}
  };

  const setSelectedRange = (range) => {
    setSelectedRangeState(range);
    try {
      localStorage.setItem('topazly_timeframe', range);
    } catch {}
  };

  const activeSiteConfig = getSiteById(selectedSite);

  const fetchTelemetry = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`/api/telemetry?site=${selectedSite}&range=${selectedRange}`);
      const json = await res.json();
      if (res.ok && json.ok) {
        setData(json.data);
      } else {
        setError(json.error || 'Failed to fetch telemetry metrics');
      }
    } catch (err) {
      setError(err.message || 'Network error fetching telemetry');
    } finally {
      setLoading(false);
    }
  }, [selectedSite, selectedRange, isAuthenticated]);

  useEffect(() => {
    fetchTelemetry();
  }, [fetchTelemetry]);

  // Polling for real-time updates every 30 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      fetchTelemetry();
    }, 30000);
    return () => clearInterval(timer);
  }, [fetchTelemetry]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  const summary = data?.summary || {};
  const dwellLadder = data?.dwellLadder || {};
  const timeline = data?.timeline || [];
  const actions = data?.actions || [];
  const topContent = data?.topContent || [];
  const referrers = data?.referrers || [];
  const devices = data?.devices || [];
  const countries = data?.countries || [];
  const liveStream = data?.liveStream || [];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex flex-col transition-colors">
      {/* Top Sticky Header */}
      <Header
        selectedRange={selectedRange}
        onSelectRange={setSelectedRange}
        onRefresh={fetchTelemetry}
        loading={loading}
      />

      {/* Horizontal Site Navigation */}
      <SiteNav selectedSite={selectedSite} onSelectSite={setSelectedSite} />

      {/* Main Container */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-5 sm:py-6 space-y-5 sm:space-y-6">
        {/* Site Header Description */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200/60 dark:border-slate-800 pb-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                {activeSiteConfig.name}
              </h1>
              <span className="text-xs px-2 py-0.5 rounded-md font-mono bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                {activeSiteConfig.domain}
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {activeSiteConfig.description}
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 font-mono">
            <span>Range: {selectedRange.toUpperCase()}</span>
            <span>·</span>
            <span>Dataset: {activeSiteConfig.dataset || 'network-aggregate'}</span>
          </div>
        </div>

        {/* Error notification banner */}
        {error && (
          <div className="p-3.5 rounded-xl bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-200 text-xs flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* KPI Cards Row (4 cards, responsive grid) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <MetricCard
            title="Unique Visitors"
            value={summary.uniqueVisitors || 0}
            subtext="First-party distinct users"
            icon={Users}
            color="emerald"
          />
          <MetricCard
            title="Total Pageviews"
            value={summary.pageviews || 0}
            subtext="Zero-pixel edge verified"
            icon={Eye}
            color="blue"
          />
          <MetricCard
            title="Conversion Actions"
            value={summary.conversionActions || 0}
            subtext="Prints, clicks, downloads"
            icon={Zap}
            color="indigo"
          />
          <MetricCard
            title="Avg Active Dwell"
            value={`${summary.avgDwellSeconds || 0}s`}
            subtext="In-viewport engagement"
            icon={Timer}
            color="amber"
          />
        </div>

        {/* Timeline Chart */}
        <TimelineChart data={timeline} range={selectedRange} />

        {/* Two-Column Mid Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Action Leaderboard */}
          <ActionLeaderboard actions={actions} siteConfig={activeSiteConfig} />

          {/* Dwell Ladder Card (if site supports dwell) or Discovery */}
          {activeSiteConfig.hasDwell ? (
            <DwellLadderCard dwell={dwellLadder} />
          ) : (
            <DiscoveryCard referrers={referrers} countries={countries} devices={devices} />
          )}
        </div>

        {/* Lower Grid: Top Content, Discovery, and Live Stream */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="lg:col-span-1">
            <TopContentCard content={topContent} siteConfig={activeSiteConfig} />
          </div>

          <div className="lg:col-span-1">
            <DiscoveryCard referrers={referrers} countries={countries} devices={devices} />
          </div>

          <div className="lg:col-span-1">
            <LiveFeedCard stream={liveStream} />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-auto border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 py-4 text-center text-xs text-slate-400">
        Topazly Studio · stats.topazly.com · Cloudflare Workers Analytics Engine
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <DashboardContent />
      </AuthProvider>
    </ThemeProvider>
  );
}
